import { downloadDocument } from 'Cloud/Application/Editor/MyOffice/helpers/downloadDocument';
import config from 'Cloud/config';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { sendXray } from 'reactApp/utils/ga';

import styles from './ReactPdfViewer.css';

const pathToWorker = `${config.get('BUILD_URLS').js}/pdf.worker.min.js`;

pdfjs.GlobalWorkerOptions.workerSrc = pathToWorker;

function isBuffer(data: any): data is Buffer {
    return Boolean(data && data.byteLength);
}

export const ReactPdfViewer: FC<{ url: string }> = ({ url }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [error, setError] = useState('');
    const [content, setContent] = useState<Uint8Array>(new Uint8Array());
    const [numPages, setNumPages] = useState(0);

    useEffect(() => {
        downloadDocument(url).then(({ content: data, error }) => {
            if (data && isBuffer(data)) {
                setContent(new Uint8Array(data));
                return;
            }
            if (error) {
                // @ts-ignore
                setError(error.toString());
                return;
            }

            setError('Something went wrong');
        });
    }, []);

    useEffect(() => {
        sendXray(['viewer', 'pdf', 'open']);
    }, []);

    useEffect(() => {
        if (error) {
            sendXray(['viewer', 'pdf', 'error', error]);
        }
    }, [error]);

    const file = useMemo(() => ({ data: content }), [content]);

    const onLoadSuccess = useCallback(({ numPages }: PDFDocumentProxy) => {
        setNumPages(numPages);
    }, []);

    const onLoadError = useCallback((error: Error) => {
        setError(error.message);
    }, []);

    return (
        <>
            {content.length && !error && (
                <div ref={ref} className={styles.root} body-scroll-lock-ignore="true">
                    <Document file={file} onLoadSuccess={onLoadSuccess} error="" onLoadError={onLoadError} onSourceError={onLoadError}>
                        {numPages &&
                            new Array(numPages)
                                .fill('')
                                .map((item, key) => <Page width={ref.current?.offsetWidth} key={key} pageNumber={key + 1} />)}
                    </Document>
                </div>
            )}
            {error && <div className={styles.text}>{error}</div>}
            {!content.length && !error && <Spinner />}
        </>
    );
};
